// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ClearSearchIcon from "@mui/icons-material/Cancel";

import ProjectDetails from "./ProjectDetails";
import SearchInput from "./SearchInput";
import { TestConditions, type AIModelSetupProps, type TestItem } from "../types/projects";
import { createStyleSheet } from "../utils/styles";
import NewTests from "./NewTests";
import AudereSupportedTests from "./AudereSupportedTests";
import { useListRDTsQuery } from "../features/data/projectApiSlice";
import BottomBasket from "./BottomBasket";
import { theme } from "../../theme";
import { getErrorText } from "../utils/error";
import { useAppDispatch } from "../features/store";
import { getSelectedTestIds, persistSelectedTestIds } from "../features/data/projectSlice";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";

const AIModelSetup = (props: AIModelSetupProps) => {
  const {
    handleStep,
    projectDetails,
    onContinue,
    error: apiError,
    onAddNewTest,
    setNewTests,
    newTests,
    currentProjectData,
    onCancel,
  } = props;
  const { t } = useTranslation("AIModelSetup");
  const dispatch = useAppDispatch();
  const persistedSelectedTestIds = useSelector(getSelectedTestIds);
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { data, error, isError, refetch } = useListRDTsQuery();

  const [searchValue, setSearchValue] = useState("");
  const [selectedTestIds, setSelectedTestIds] = useState<string[]>([]);

  // Sorting TestItems by last_modified
  const sortTestItems = (t1: TestItem, t2: TestItem): number => {
    const t1Time = t1.last_modified ? new Date(t1.last_modified).getTime() : 0;
    const t2Time = t2.last_modified ? new Date(t2.last_modified).getTime() : 0;

    // If last_modified times are the same, sort by brand + model + condition
    if (t1Time === t2Time) {
      const t1Key = `${t1.brand} ${t1.model} ${t1.condition}`;
      const t2Key = `${t2.brand} ${t2.model} ${t2.condition}`;
      return t1Key.localeCompare(t2Key);
    }

    return t2Time - t1Time; // Most recent first
  };

  const handleEditProject = useCallback(() => {
    handleStep(0, undefined);
  }, [handleStep]);
  const onSearchChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(target.value);
  };
  const handleSearchClearClick = useCallback(() => {
    setSearchValue("");
  }, []);
  const handleSelectionChange = (newSelectedIds: string[]) => {
    setSelectedTestIds(newSelectedIds);
    dispatch(persistSelectedTestIds(newSelectedIds));
  };
  const handleRemoveTest = (id: string) => {
    setSelectedTestIds(prev => {
      const updated = prev.filter(testId => testId !== id);
      dispatch(persistSelectedTestIds(updated));
      return updated;
    });
  };
  const groupedRDTsByCondition = useMemo(() => {
    const rdts = data?.rdts ?? [];
    if (!rdts.length) return [];

    const lowerFilter = searchValue.trim().toLowerCase();

    // Filter first: exclude current org + search filter match
    const filtered = rdts.filter(({ brand, model, condition, organization_uid }) => {
      if (organization_uid === params.orgUid) return false;
      const haystack = `${brand} ${model} ${condition}`.toLowerCase();
      return haystack.includes(lowerFilter);
    });

    const groupedMap = new Map<string, TestItem[]>([
      [TestConditions.HIV, []],
      [TestConditions.Malaria, []],
      [TestConditions.FOUNDATION, []],
      [TestConditions.Others, []],
    ]);

    // Group based on whether the condition includes "malaria" or "hiv"
    filtered.forEach(item => {
      const { brand, model, condition } = item;
      const conditionLower = condition.toLowerCase();
      if (brand === "Standard" && model === "Standard" && condition === "Generic") {
        groupedMap.get(TestConditions.FOUNDATION)!.push(item);
      } else if (conditionLower.includes("hiv")) {
        groupedMap.get(TestConditions.HIV)!.push(item);
      } else if (conditionLower.includes("malaria")) {
        groupedMap.get(TestConditions.Malaria)!.push(item);
      } else {
        groupedMap.get(TestConditions.Others)!.push(item);
      }
    });

    // Sort each group by last_modified (recent first) and convert to array
    return Array.from(groupedMap.entries())
      .filter(([, rdts]) => rdts.length > 0)
      .map(([condition, rdts]) => ({
        condition,
        rdts: rdts.sort(sortTestItems),
      }));
  }, [data?.rdts, searchValue, params.orgUid]);
  const selectedTestItems = useMemo(() => {
    const selectedSet = new Set(selectedTestIds);
    return [...(data?.rdts?.filter(item => selectedSet.has(item.rdt_uid)) || [])];
  }, [data, selectedTestIds]);
  const handleContinue = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onContinue(selectedTestItems);
    },
    [onContinue, selectedTestItems]
  );

  useEffect(() => {
    if (persistedSelectedTestIds.length > 0) {
      setSelectedTestIds(prev => [...new Set([...prev, ...persistedSelectedTestIds])]);
    }
  }, [persistedSelectedTestIds]);

  useEffect(() => {
    const rdts = currentProjectData?.project?.rdts?.map(rdt => rdt.rdt_uid) ?? [];
    if (rdts.length > 0) {
      setSelectedTestIds(prev => [...new Set([...prev, ...rdts])]);
    }
  }, [currentProjectData]);
  useEffect(() => {
    const rdts = data?.rdts ?? [];
    if (!rdts.length) {
      setNewTests([]);
      return;
    }
    const lowerFilter = searchValue.trim().toLowerCase();
    const filteredAndSortedNewTests = rdts
      .filter(({ brand, model, condition, organization_uid }) => {
        if (organization_uid !== params.orgUid) return false;
        const haystack = `${brand} ${model} ${condition}`.toLowerCase();
        return haystack.includes(lowerFilter);
      })
      .sort(sortTestItems);

    setNewTests(filteredAndSortedNewTests);
  }, [data?.rdts, searchValue, params.orgUid, setNewTests]);
  useEffect(() => {
    const newTestUid = location.state?.newTestUid;
    if (!newTestUid) return;
    if (!selectedTestIds.includes(newTestUid)) {
      const newSelectedIds = [
        ...new Set([...selectedTestIds, newTestUid, ...persistedSelectedTestIds]),
      ];
      setSelectedTestIds(newSelectedIds);
      dispatch(persistSelectedTestIds(newSelectedIds));
      refetch();
    }
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: {} }
    );
  }, [location, selectedTestIds, dispatch, navigate, refetch, persistedSelectedTestIds]);

  return (
    <Box sx={styles.aiModelContainer}>
      <Typography variant="h6" fontSize={40}>
        {t("aiModelSetupHeader")}
      </Typography>
      {(isError || apiError) && <Alert severity="error">{getErrorText(error || apiError)}</Alert>}
      <ProjectDetails projectDetails={projectDetails} handleEditProject={handleEditProject} />
      <SearchInput
        inputProps={{
          placeholder: t("searchPlaceholder"),
          value: searchValue,
          endAdornment: searchValue ? (
            <IconButton onClick={handleSearchClearClick} sx={{ marginRight: 1 }}>
              <Tooltip title={t("common:clear")}>
                <ClearSearchIcon />
              </Tooltip>
            </IconButton>
          ) : undefined,
          onChange: onSearchChange,
        }}
      />
      <NewTests
        newTests={newTests}
        onAddNewTest={onAddNewTest}
        selectedTestIds={selectedTestIds}
        onToggleNewTestSelection={handleSelectionChange}
      />
      <AudereSupportedTests
        audereSupportedTests={groupedRDTsByCondition}
        selectedTestIds={selectedTestIds}
        onSelectionChange={handleSelectionChange}
      />
      <BottomBasket
        testItems={selectedTestItems}
        onContinue={handleContinue}
        onRemove={handleRemoveTest}
        onCancel={onCancel}
      />
    </Box>
  );
};

export default AIModelSetup;

const styles = createStyleSheet({
  aiModelContainer: {
    px: 2,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    pb: theme.spacing(10),
  },
});
